import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PhotoIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TagIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  CubeIcon,
  SparklesIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { fetchCatalog, fetchMySupplierProfile, createListing, updateListing, fetchListingDetail } from '../api/marketplaceApi';
import { apiPost } from '../../../utils/api';
import { API_URL } from '../../../api/config';
import type { CatalogProduct, Listing } from '../types';

interface EditorForm {
  catalog_product_id: number;
  title: string;
  description: string;
  grade: string;
  certification: string;
  commercial_unit: string;
  normalized_unit: string;
  conversion_factor: number;
  quantity_step: number;
  price_per_unit: number;
  currency: string;
  minimum_order_quantity: number;
  maximum_order_quantity: string;
  available_quantity: number;
  fulfillment_location: string;
  fulfillment_city: string;
  delivery_areas: string;
  harvest_start_date: string;
  harvest_end_date: string;
  traceability_enabled: boolean;
}

const blankForm: EditorForm = {
  catalog_product_id: 0,
  title: '',
  description: '',
  grade: '',
  certification: '',
  commercial_unit: 'kg',
  normalized_unit: 'kg',
  conversion_factor: 1,
  quantity_step: 0.5,
  price_per_unit: 0,
  currency: 'RWF',
  minimum_order_quantity: 1,
  maximum_order_quantity: '',
  available_quantity: 0,
  fulfillment_location: '',
  fulfillment_city: '',
  delivery_areas: '',
  harvest_start_date: '',
  harvest_end_date: '',
  traceability_enabled: false,
};

const toDateInputValue = (value?: string | null) => value ? String(value).slice(0, 10) : '';

const formatValidationError = (error: any, fallback: string) => {
  const details = Array.isArray(error?.details) ? error.details : [];
  if (details.length === 0) return error?.message || fallback;

  const labels: Record<string, string> = {
    catalog_product_id: 'Product',
    minimum_order_quantity: 'Minimum order quantity',
    maximum_order_quantity: 'Maximum order quantity',
    available_quantity: 'Available quantity',
    quantity_step: 'Order increment',
    harvest_start_date: 'Harvest start date',
    harvest_end_date: 'Harvest end date',
    delivery_areas: 'Delivery areas',
  };
  const constraints: Record<string, string> = {
    positive_number: 'must be greater than zero',
    non_negative_number: 'cannot be negative',
    aligned_to_quantity_step: 'must be a multiple of the order increment',
    not_below_minimum: 'cannot be lower than the minimum order',
    iso_date: 'must be a valid date',
    not_before_start: 'cannot be before the harvest start date',
    valid_json_array: 'must contain valid delivery areas',
    active_catalog_product: 'is no longer active; select an active product',
  };

  return details.map((detail: any) => {
    const field = labels[detail?.field] || String(detail?.field || 'Field');
    const explanation = detail?.message || constraints[detail?.constraint] || String(detail?.constraint || 'is invalid');
    return `${field} ${explanation}`;
  }).join('. ');
};

const toForm = (l: Listing): EditorForm => ({
  catalog_product_id: l.catalog_product_id,
  title: l.title || '',
  description: l.description || '',
  grade: l.grade || '',
  certification: l.certification || '',
  commercial_unit: l.commercial_unit || 'kg',
  normalized_unit: l.normalized_unit || 'kg',
  conversion_factor: l.conversion_factor || 1,
  quantity_step: l.quantity_step || 1,
  price_per_unit: l.price_per_unit || 0,
  currency: l.currency || 'RWF',
  minimum_order_quantity: l.minimum_order_quantity || 1,
  maximum_order_quantity: l.maximum_order_quantity != null ? String(l.maximum_order_quantity) : '',
  available_quantity: l.available_quantity || 0,
  fulfillment_location: l.fulfillment_location || '',
  fulfillment_city: l.fulfillment_city || '',
  delivery_areas: Array.isArray(l.delivery_areas) ? l.delivery_areas.join(', ') : '',
  harvest_start_date: toDateInputValue(l.harvest_start_date),
  harvest_end_date: toDateInputValue(l.harvest_end_date),
  traceability_enabled: !!l.traceability_enabled,
});

const ListingEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const listingId = isEdit ? Number(id) : null;
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [catalogProducts, setCatalogProducts] = useState<CatalogProduct[]>([]);
  const [verified, setVerified] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  const [form, setForm] = useState<EditorForm>(blankForm);

  useEffect(() => {
    Promise.all([
      fetchCatalog({ limit: 100 }),
      fetchMySupplierProfile(),
    ]).then(([catalog, profile]) => {
      setCatalogProducts(catalog.items || []);
      setVerified(profile.profile?.verification_status === 'verified');
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit || listingId == null) return;
    setLoading(true);
    fetchListingDetail(listingId)
      .then((res) => {
        const item = (res?.item || res?.listing || res) as Listing;
        setForm(toForm(item));
        setExistingImageUrl(item.image_url || null);
        setDirty(false);
      })
      .catch((err) => setLoadError(err?.message || 'Failed to load listing'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Warn before leaving with unsaved changes.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const setField = (patch: Partial<EditorForm>) => {
    setForm((f) => ({ ...f, ...patch }));
    setDirty(true);
  };

  const setTargetUnitDefaults = (unit: string) => {
    const autoStep = ['kg', 'tonne', 'litre'].includes(unit) ? 0.5 : 1;
    setForm((f) => ({ ...f, commercial_unit: unit, quantity_step: autoStep }));
    setDirty(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    if (isEdit) setExistingImageUrl(null);
    setRemoveImage(false);
    setError(null);
    setDirty(true);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (isEdit) {
      setExistingImageUrl(null);
      setRemoveImage(true);
    }
    setDirty(true);
  };

  const validate = (): string | null => {
    if (!form.title || !form.catalog_product_id) return 'Please fill in all required fields';
    if (!Number.isFinite(Number(form.price_per_unit)) || Number(form.price_per_unit) <= 0) return 'Price per unit must be a positive number';
    if (!Number.isFinite(Number(form.available_quantity)) || Number(form.available_quantity) < 0) return 'Available quantity must be a non-negative number';
    if (!Number.isFinite(Number(form.quantity_step)) || Number(form.quantity_step) <= 0) return 'Order increments must be a positive number';
    if (!Number.isFinite(Number(form.minimum_order_quantity)) || Number(form.minimum_order_quantity) <= 0) return 'Minimum order quantity must be a positive number';
    const step = Number(form.quantity_step);
    const minimumRatio = Number(form.minimum_order_quantity) / step;
    if (Math.abs(minimumRatio - Math.round(minimumRatio)) > 1e-6) return 'Minimum order quantity must be a multiple of the order increment';
    if (Number(form.available_quantity) > 0) {
      const availableRatio = Number(form.available_quantity) / step;
      if (Math.abs(availableRatio - Math.round(availableRatio)) > 1e-6) return 'Available quantity must be a multiple of the order increment';
    }
    if (form.maximum_order_quantity !== '' && Number(form.maximum_order_quantity) < Number(form.minimum_order_quantity)) return 'Maximum order cannot be lower than minimum order';
    if (!/^[A-Z]{3}$/.test(form.currency)) return 'Currency must be a 3-letter ISO code';
    if (form.harvest_start_date && form.harvest_end_date && form.harvest_start_date > form.harvest_end_date) return 'Harvest start date cannot be after the end date';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verified) { setError('You must be a verified supplier to create listings'); return; }
    const validationError = validate();
    if (validationError) { setError(validationError); return; }
    setSubmitting(true);
    setError(null);
    try {
      let catalogProductId = form.catalog_product_id;

      // If supplier product (negative ID), create catalog product first.
      if (catalogProductId < 0) {
        const supplierProduct = catalogProducts.find((p) => p.id === catalogProductId);
        const created = await createListingAsCatalog(supplierProduct?.name || form.title);
        catalogProductId = created;
      }

      const formData = new FormData();
      formData.append('catalog_product_id', String(catalogProductId));
      formData.append('title', form.title);
      formData.append('description', form.description || '');
      formData.append('grade', form.grade || '');
      formData.append('certification', form.certification || '');
      formData.append('commercial_unit', form.commercial_unit);
      formData.append('normalized_unit', form.normalized_unit || form.commercial_unit);
      formData.append('conversion_factor', String(form.conversion_factor || 1));
      formData.append('price_per_unit', String(form.price_per_unit));
      formData.append('currency', form.currency || 'RWF');
      formData.append('minimum_order_quantity', String(form.minimum_order_quantity));
      if (form.maximum_order_quantity !== '' || isEdit) formData.append('maximum_order_quantity', String(form.maximum_order_quantity));
      formData.append('available_quantity', String(form.available_quantity));
      formData.append('quantity_step', String(form.quantity_step));
      formData.append('fulfillment_location', form.fulfillment_location || '');
      formData.append('fulfillment_city', form.fulfillment_city || '');
      const areas = form.delivery_areas.split(',').map((s) => s.trim()).filter(Boolean);
      if (areas.length > 0 || isEdit) formData.append('delivery_areas', JSON.stringify(areas));
      if (form.harvest_start_date || isEdit) formData.append('harvest_start_date', form.harvest_start_date);
      if (form.harvest_end_date || isEdit) formData.append('harvest_end_date', form.harvest_end_date);
      formData.append('traceability_enabled', form.traceability_enabled ? 'true' : 'false');
      if (imageFile) formData.append('image', imageFile);
      if (isEdit && removeImage) formData.append('remove_image', 'true');

      if (isEdit && listingId != null) {
        await updateListing(listingId, formData);
      } else {
        await createListing(formData);
      }

      setSuccess(true);
      setDirty(false);
      setTimeout(() => navigate(isEdit ? `/marketplace/supplier/listings/${listingId}` : '/marketplace/supplier/listings'), 1000);
    } catch (err: any) {
      setError(formatValidationError(err, isEdit ? 'Failed to update listing' : 'Failed to create listing'));
    } finally {
      setSubmitting(false);
    }
  };

  // Helper: create a standalone catalog product (for "supplier product" option).
  const createListingAsCatalog = async (name: string): Promise<number> => {
    const res: any = await apiPost('/api/marketplace/b2b/catalog', { name, category: 'Supplier Product', base_unit: 'kg' });
    return res?.item?.id || res?.id;
  };

  const selectedProduct = catalogProducts.find((p) => p.id === form.catalog_product_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="relative">
          <div className="w-12 h-12 border-4 border-emerald-200 rounded-full"></div>
          <div className="w-12 h-12 border-4 border-emerald-600 rounded-full border-t-transparent animate-spin absolute top-0 left-0"></div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeftIcon className="w-4 h-4" /> Back to listings
        </button>
        <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-red-400 mx-auto" />
          <p className="text-red-700 font-semibold mt-3">{loadError}</p>
          <button onClick={() => navigate('/marketplace/supplier/listings')} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700">
            Back to listings
          </button>
        </div>
      </div>
    );
  }

  const previewSrc = imagePreview || (isEdit && existingImageUrl ? (existingImageUrl.startsWith('http') ? existingImageUrl : `${API_URL}${existingImageUrl}`) : null);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors">
        <ArrowLeftIcon className="w-4 h-4" /> Back to listings
      </button>

      <div className="relative overflow-hidden bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 rounded-2xl p-8 text-white">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-80 h-80 bg-white rounded-full -translate-y-1/2 translate-x-1/4"></div>
          <div className="absolute bottom-0 left-0 w-60 h-60 bg-white rounded-full translate-y-1/2 -translate-x-1/4"></div>
        </div>
        <div className="relative flex items-center gap-4">
          <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
            {isEdit ? <PencilSquareIcon className="w-8 h-8" /> : <SparklesIcon className="w-8 h-8" />}
          </div>
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight">{isEdit ? 'Edit Listing' : 'Create New Listing'}</h1>
            <p className="text-emerald-100 mt-1">{isEdit ? 'Update your product listing details' : 'List your products on the AAYWA Marketplace'}</p>
          </div>
        </div>
      </div>

      {!verified && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-start gap-3">
          <ExclamationTriangleIcon className="w-6 h-6 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-amber-800">Verification Required</p>
            <p className="text-sm text-amber-700 mt-1">You must complete supplier verification before creating listings. <button onClick={() => navigate('/marketplace/supplier/profile')} className="underline font-semibold">Go to Profile</button></p>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-center gap-3">
          <CheckCircleIcon className="w-6 h-6 text-emerald-500" />
          <div>
            <p className="font-bold text-emerald-800">{isEdit ? 'Listing Updated!' : 'Listing Created!'}</p>
            <p className="text-sm text-emerald-700">Redirecting...</p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5 flex items-center gap-3">
          <ExclamationTriangleIcon className="w-6 h-6 text-red-500" />
          <p className="text-sm text-red-700 font-medium flex-1">{error}</p>
          <button onClick={() => setError(null)} className="p-1 hover:bg-red-100 rounded-lg"><XMarkIcon className="w-4 h-4 text-red-400" /></button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h3 className="text-lg font-extrabold text-gray-900 mb-1">Product Image</h3>
          <p className="text-sm text-gray-500 mb-4">{isEdit ? 'Replace or remove the current image. Leaving it unchanged preserves the existing photo.' : 'Upload a high-quality image of your product. This will be displayed on the marketplace.'}</p>
          <div
            onClick={() => fileInputRef.current?.click()}
            className={`relative border-2 border-dashed rounded-2xl cursor-pointer transition-all overflow-hidden ${
              previewSrc
                ? 'border-emerald-300 bg-emerald-50/50'
                : 'border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/30'
            }`}
          >
            {previewSrc ? (
              <div className="relative">
                <img src={previewSrc} alt="Preview" className="w-full h-64 object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                  <div className="text-white">
                    <p className="font-bold text-sm">{imageFile?.name || 'Current image'}</p>
                    <p className="text-xs opacity-80">{imageFile ? (imageFile.size / 1024 / 1024).toFixed(2) + ' MB' : ''}</p>
                  </div>
                  <button
                    type="button"
                    onClick={(ev) => { ev.stopPropagation(); handleRemoveImage(); }}
                    className="p-2 bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 text-white transition-all"
                    aria-label="Remove image"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="py-16 text-center">
                <PhotoIcon className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                <p className="font-bold text-gray-700">Click to upload product image</p>
                <p className="text-sm text-gray-400 mt-1">PNG, JPG, WebP up to 5MB</p>
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="hidden"
          />
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h3 className="text-lg font-extrabold text-gray-900 mb-4">Product Details</h3>
          <div className="space-y-4">
            <div>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2">
                <TagIcon className="w-4 h-4 text-gray-400" />
                Product Catalogue *
              </label>
              <select
                value={form.catalog_product_id}
                required
                disabled={isEdit}
                onChange={e => setField({ catalog_product_id: parseInt(e.target.value) })}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:ring-0 outline-none transition-all disabled:opacity-60"
              >
                <option value={0}>Select a product...</option>
                {catalogProducts.map(p => (
                  <option key={p.id} value={p.id}>{p.name} ({p.category})</option>
                ))}
              </select>
              {isEdit && <p className="text-xs text-gray-400 mt-1">Product cannot be changed after creation.</p>}
              {selectedProduct && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-semibold">{selectedProduct.category}</span>
                  {selectedProduct.subcategory && <span className="px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-xs font-semibold">{selectedProduct.subcategory}</span>}
                </div>
              )}
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2">
                <CubeIcon className="w-4 h-4 text-gray-400" />
                Listing Title *
              </label>
              <input
                type="text"
                required
                value={form.title}
                onChange={e => setField({ title: e.target.value })}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:ring-0 outline-none transition-all"
                placeholder="e.g., Premium Arabica Coffee Beans"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Description</label>
              <textarea
                value={form.description}
                onChange={e => setField({ description: e.target.value })}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:ring-0 outline-none resize-none transition-all"
                rows={4}
                placeholder="Describe your product's quality, origin, processing method, flavour profile..."
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h3 className="text-lg font-extrabold text-gray-900 mb-4">Specifications</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Grade</label>
              <input
                type="text"
                value={form.grade}
                onChange={e => setField({ grade: e.target.value })}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:ring-0 outline-none transition-all"
                placeholder="e.g., AA, AB, Grade 1"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Unit *</label>
              <select
                value={form.commercial_unit}
                onChange={e => setTargetUnitDefaults(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:ring-0 outline-none transition-all"
              >
                <option value="kg">Kilogram (kg)</option>
                <option value="tonne">Metric Tonne</option>
                <option value="box">Box</option>
                <option value="crate">Crate</option>
                <option value="bag">Bag</option>
                <option value="piece">Piece</option>
                <option value="litre">Litre</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Certification</label>
              <input
                type="text"
                value={form.certification}
                onChange={e => setField({ certification: e.target.value })}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:ring-0 outline-none transition-all"
                placeholder="e.g., Organic, Fair Trade"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h3 className="text-lg font-extrabold text-gray-900 mb-4">Pricing & Availability</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2">
                <CurrencyDollarIcon className="w-4 h-4 text-gray-400" />
                Price per Unit *
              </label>
              <div className="relative">
                <input
                  type="number"
                  required
                  min={0}
                  step={0.01}
                  value={form.price_per_unit || ''}
                  onChange={e => setField({ price_per_unit: parseFloat(e.target.value) || 0 })}
                  className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:ring-0 outline-none transition-all"
                  placeholder="0"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-bold text-gray-400">{form.currency}</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Min. Order Qty *</label>
              <input
                type="number"
                min={0}
                step={0.05}
                value={form.minimum_order_quantity}
                onChange={e => setField({ minimum_order_quantity: parseFloat(e.target.value) || 1 })}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:ring-0 outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Max. Order Qty</label>
              <input
                type="number"
                min={0}
                step={0.05}
                value={form.maximum_order_quantity}
                onChange={e => setField({ maximum_order_quantity: e.target.value })}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:ring-0 outline-none transition-all"
                placeholder="Optional"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Available Qty *</label>
              <input
                type="number"
                required
                min={0}
                step={0.05}
                value={form.available_quantity || ''}
                onChange={e => setField({ available_quantity: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:ring-0 outline-none transition-all"
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Order Increments *</label>
              <input
                type="number"
                min={0.05}
                step={0.05}
                value={form.quantity_step}
                onChange={e => setField({ quantity_step: parseFloat(e.target.value) || 1 })}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:ring-0 outline-none transition-all"
              />
              <p className="text-xs text-gray-400 mt-1">Buyers can order in multiples of this amount (auto-set from unit).</p>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Currency</label>
              <input
                type="text"
                maxLength={3}
                value={form.currency}
                onChange={e => setField({ currency: e.target.value.toUpperCase() })}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:ring-0 outline-none transition-all uppercase"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h3 className="text-lg font-extrabold text-gray-900 mb-4">Harvest & Delivery</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Harvest Start</label>
              <input
                type="date"
                value={form.harvest_start_date}
                onChange={e => setField({ harvest_start_date: e.target.value })}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:ring-0 outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Harvest End</label>
              <input
                type="date"
                value={form.harvest_end_date}
                onChange={e => setField({ harvest_end_date: e.target.value })}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:ring-0 outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Delivery Areas</label>
              <input
                type="text"
                value={form.delivery_areas}
                onChange={e => setField({ delivery_areas: e.target.value })}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:ring-0 outline-none transition-all"
                placeholder="Kigali, Huye, Musanze (comma separated)"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h3 className="text-lg font-extrabold text-gray-900 mb-4">Fulfillment Location</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2">
                <MapPinIcon className="w-4 h-4 text-gray-400" />
                Address
              </label>
              <input
                type="text"
                value={form.fulfillment_location}
                onChange={e => setField({ fulfillment_location: e.target.value })}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:ring-0 outline-none transition-all"
                placeholder="Warehouse or farm address"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">City</label>
              <input
                type="text"
                value={form.fulfillment_city}
                onChange={e => setField({ fulfillment_city: e.target.value })}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:border-emerald-500 focus:ring-0 outline-none transition-all"
                placeholder="e.g., Kigali, Huye"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-extrabold text-gray-900">Traceability</h3>
              <p className="text-sm text-gray-500 mt-1">Link this listing to harvest lots for full supply chain transparency</p>
            </div>
            <button
              type="button"
              onClick={() => setField({ traceability_enabled: !form.traceability_enabled })}
              className={`relative w-12 h-7 rounded-full transition-colors ${form.traceability_enabled ? 'bg-emerald-500' : 'bg-gray-200'}`}
              aria-pressed={form.traceability_enabled}
            >
              <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${form.traceability_enabled ? 'translate-x-5' : ''}`} />
            </button>
          </div>
        </div>

        <div className="flex items-center justify-end gap-4 pt-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-3 text-sm font-bold text-gray-600 hover:bg-gray-100 rounded-xl transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !verified}
            className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 hover:to-teal-700 font-bold text-sm shadow-lg shadow-emerald-200 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {isEdit ? 'Saving...' : 'Creating...'}
              </>
            ) : (
              <>
                <CheckCircleIcon className="w-5 h-5" />
                {isEdit ? 'Save Changes' : 'Create Listing'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ListingEditor;
